import { useEffect, useState } from 'react';

import { TCustomer } from 'types/customer';

import { CustomerProfileResp } from '@api/responseModels/customer/customerProfileResponse';
import { InvitationResp } from '@api/responseModels/invitation/invitationResponse';
import { CurrentUserResponse } from '@api/responseModels/user/currentUserResponse';
import { CRMAPIManager } from '@classes/crmApiManager';
import { messageService } from '@classes/messageService';
import { SettingsManager } from '@classes/settingsManager';
import { CustomerPickerTabs } from '@organisms/orders/common/customerPickerTabs';

import { TOrderCustomer } from '../createOrderModal';

const CUSTOMER_LIST_ID = 'create-order-customer-list';

interface Props {
    selectedOrderCustomer: TOrderCustomer;
    setSelectedOrderCustomer: React.Dispatch<React.SetStateAction<TOrderCustomer>>;
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
    createCustomerIfNotExist?: boolean;
    /** Поле поиска живёт в шапке модалки, сюда приходит уже готовый запрос. */
    searchQuery: string;
}

export const CustomerStep: React.FC<Props> = ({
    selectedOrderCustomer,
    setSelectedOrderCustomer,
    setCurrentStep,
    createCustomerIfNotExist,
    searchQuery,
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleCreateCustomerProfile = async () => {
        try {
            setIsLoading(true);
            const creds = SettingsManager.getConnectionCredentials();

            // Получение текущего пользователя
            const currentResp = await CRMAPIManager.request<CurrentUserResponse>(async (api) => {
                return await api.getCurrentUser(creds.crmID);
            });

            // Получение телефона текущего пользователя
            const currentUserPhone = currentResp.data?.data?.phone;

            // Создание профиля клиента для текущего пользователя
            const inv = await CRMAPIManager.request<InvitationResp>(async (api) => {
                return await api.inviteRegisterUser(creds.crmID, currentUserPhone, 'customer');
            });

            if (inv.errorMessages) throw new Error(inv.errorMessages[0]);

            // Получение профиля клиента текущего пользователя. `data.id` — это id
            // приглашения, профиль клиента лежит в `data.customer`
            const currentWithCustomerResp = await CRMAPIManager.request<CustomerProfileResp>(
                async (api) => {
                    return await api.getCustomerProfile(inv.data?.data?.customer?.id, creds.crmID);
                }
            );
            messageService.sendSuccess('Профиль клиента создан');
            setSelectedOrderCustomer(currentWithCustomerResp.data?.data);
            setCurrentStep(1);
        } catch (err) {
            messageService.sendError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Галочка — единственное, что выбирает клиента, и она же ведёт дальше:
     * отдельной кнопки «Далее» внизу модалки больше нет.
     */
    const handleSelectCustomer = (customer: TCustomer) => {
        setSelectedOrderCustomer(customer);
        setCurrentStep(1);
    };

    useEffect(() => {
        if (createCustomerIfNotExist && !selectedOrderCustomer) {
            handleCreateCustomerProfile();
        }
    }, [createCustomerIfNotExist, !selectedOrderCustomer]);

    return (
        <CustomerPickerTabs
            listId={CUSTOMER_LIST_ID}
            searchQuery={searchQuery}
            selectedCustomerId={selectedOrderCustomer?.id}
            onSelect={handleSelectCustomer}
            isBusy={isLoading}
        />
    );
};
